import React, { useEffect, useState } from 'react';
import { ModalShell } from '../ds/ModalShell';
import { InputField } from '../ds/InputField';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import { termsApi, ScheduleMailResult } from '../services/api';
import { emailError } from '../utils/clientFields';
import { budynkiLabel } from '../utils/plural';

export interface ScheduleMailModalProps {
  /** Budynki, których grafik idzie mailem; `null` = modal zamknięty. */
  buildingIds: string[] | null;
  /** Podpowiedź adresata (e-mail wspólnego zarządcy zaznaczonych budynków) — pusty = brak. */
  defaultTo?: string;
  /** Nazwa zarządcy, z którego wziął się `defaultTo` (opis pod polem). */
  defaultToLabel?: string;
  onClose: () => void;
  /** Po udanej wysyłce — rodzic pokazuje potwierdzenie i zamyka modal. */
  onSent: (result: ScheduleMailResult) => void;
}

const MAX_RECIPIENTS = 5;

/** Ta sama reguła co serwer (termsQuery.mailBodyError): 1–5 adresów rozdzielonych `,`/`;`. */
export function recipientsError(raw: string): string | null {
  const list = raw.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
  if (list.length === 0) return 'Podaj adres e-mail odbiorcy';
  if (list.length > MAX_RECIPIENTS) return `Za dużo adresów: maksymalnie ${MAX_RECIPIENTS} odbiorców`;
  const bad = list.find((a) => emailError(a) !== null || /[<>"]/.test(a));
  return bad ? `Nieprawidłowy adres e-mail: ${bad} (oczekiwany format: nazwa@domena.pl)` : null;
}

/**
 * „Wyślij grafik e-mailem" (A8, lista Bronka 2026-10): ten sam plik Excela co „Pobierz grafik",
 * wysłany przez serwer jako załącznik. Adresat domyślnie = e-mail zarządcy, gdy wszystkie
 * zaznaczone budynki mają tego samego zarządcę z e-mailem. Temat/treść opcjonalne (puste =
 * domyślne z serwera). Gdy wysyłka jest na serwerze wyłączona, okno pokazuje komunikat serwera —
 * nigdy cichy sukces.
 */
export default function ScheduleMailModal({ buildingIds, defaultTo, defaultToLabel, onClose, onSent }: ScheduleMailModalProps) {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [toError, setToError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const key = buildingIds && buildingIds.length ? JSON.stringify(buildingIds) : '';
  // Każde otwarcie (nowe zaznaczenie) zaczyna od czystego formularza z podpowiedzią adresata.
  useEffect(() => {
    setTo(defaultTo || '');
    setSubject('');
    setMessage('');
    setToError(null);
    setError(null);
    setBusy(false);
  }, [key, defaultTo]);

  const submit = async () => {
    if (!buildingIds || busy) return;
    const invalid = recipientsError(to);
    setToError(invalid);
    if (invalid) return;
    setBusy(true);
    setError(null);
    try {
      const result = await termsApi.sendMail({ buildingIds, to, subject: subject.trim(), message: message.trim() });
      onSent(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nie udało się wysłać grafiku');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      open={!!key}
      title="Wyślij grafik e-mailem"
      width={560}
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Anuluj</Button>
          <Button variant="primary" icon="send" onClick={submit} disabled={busy} data-testid="schedule-mail-send">
            {busy ? 'Wysyłanie…' : 'Wyślij'}
          </Button>
        </>
      }
    >
      <div data-testid="schedule-mail" style={{ display: 'grid', gap: 'var(--space-4)', fontFamily: 'var(--font-sans)' }}>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          Grafik terminów {buildingIds ? budynkiLabel(buildingIds.length) : ''} jako plik Excela w załączniku —
          ten sam, co „Pobierz grafik". Budynki bez terminu (także „W kolejce”) nie trafią do pliku.
        </p>
        {error && (
          <div
            role="alert"
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-label)', background: 'var(--status-deny-bg)',
              border: 'var(--border-width) solid var(--status-deny-bar)', color: 'var(--status-deny)',
            }}
          >
            <Icon name="triangle-alert" size={18} />
            <span>{error}</span>
          </div>
        )}
        <InputField
          label="Adres e-mail odbiorcy"
          type="email"
          value={to}
          autoFocus
          onChange={(e) => { setTo(e.target.value); setToError(null); }}
          placeholder="nazwa@domena.pl"
          errorMessage={toError || undefined}
          hint={defaultTo && to.trim() === defaultTo
            ? `E-mail zarządcy${defaultToLabel ? `: ${defaultToLabel}` : ''}. Kilka adresów rozdziel przecinkiem.`
            : 'Kilka adresów (maks. 5) rozdziel przecinkiem.'}
        />
        <InputField
          label="Temat (opcjonalnie)"
          value={subject}
          maxLength={200}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Domyślnie: Grafik przeglądów kominiarskich <zakres dat>"
        />
        <InputField
          label="Treść (opcjonalnie)"
          multiline
          rows={5}
          value={message}
          maxLength={5000}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Domyślnie: krótka wiadomość z liczbą budynków i zakresem dat"
        />
      </div>
    </ModalShell>
  );
}
